/**
 * Pull a human-readable message out of an axios/fetch error.
 *
 * FastAPI's `detail` field can be:
 *   - a plain string  ("File too large (max 25MB)")
 *   - an object       ({type, loc, msg, input, url}) — from unhandled validation
 *   - an array of those objects — from Pydantic request-body validation
 *
 * If we pass any of the latter two directly to `toast.error()` (which React
 * eventually renders as a child), the whole tree crashes with
 *   "Objects are not valid as a React child (found: object with keys {type,loc,msg,input,url})".
 *
 * This helper always returns a string — never an object or array — so toasts
 * are guaranteed to render safely.
 */
export function errorMessage(err, fallback = "Something went wrong") {
  // Log the raw error so the user can open DevTools → Console and share it
  // when the toast text alone isn't enough to pinpoint the cause.
  if (typeof window !== "undefined" && window.console) {
    // eslint-disable-next-line no-console
    console.error("[api error]", {
      message: err?.message,
      code: err?.code,
      status: err?.response?.status,
      url: err?.config?.url,
      method: err?.config?.method,
      data: err?.response?.data,
    });
  }

  const detail = err?.response?.data?.detail;
  const status = err?.response?.status;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => d && typeof d === "object" ? (d.msg || d.message || "") : String(d))
      .filter(Boolean);
    if (msgs.length) return msgs.join(" · ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }

  // No response at all — network / CORS / timeout / proxy block
  if (!err?.response) {
    if (err?.code === "ECONNABORTED") return "Request timed out — server did not respond";
    if (err?.message === "Network Error") return "Network error — could not reach server (possible CORS, timeout, or proxy block). Open DevTools → Console for details.";
    if (err?.code) return `${fallback} (${err.code})`;
    return `${fallback} (no server response)`;
  }

  if (err?.message && err.message !== "Network Error") {
    return status ? `${err.message} (HTTP ${status})` : err.message;
  }
  return status ? `${fallback} (HTTP ${status})` : fallback;
}
